import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

/**
 * Hook per tracciare lo stato foreground/background dell'app.
 * 
 * - Mobile (Capacitor): usa App.addListener('appStateChange')
 * - Web: usa Page Visibility API (document.visibilitychange)
 * 
 * @returns true se l'app è in foreground, false se in background
 */
export function useAppState(): boolean {
    const [isActive, setIsActive] = useState(true);

    useEffect(() => {
        if (Capacitor.isNativePlatform()) {
            let handle: { remove: () => Promise<void> } | null = null;
            // Vedi useAppFocus: senza questo flag un cleanup che arriva prima della
            // risoluzione della promise lascia il listener agganciato.
            let cancelled = false;

            import('@capacitor/app').then(({ App }) => {
                return App.addListener('appStateChange', ({ isActive: active }) => {
                    setIsActive(active);
                });
            }).then(h => {
                handle = h;
                if (cancelled) h.remove();
            }).catch(() => {
                // @capacitor/app non disponibile — assume always active
            });

            return () => {
                cancelled = true;
                handle?.remove();
            };
        } else {
            const handler = () => {
                setIsActive(!document.hidden);
            };
            document.addEventListener('visibilitychange', handler);
            // Set initial state
            setIsActive(!document.hidden);
            return () => document.removeEventListener('visibilitychange', handler);
        }
    }, []);

    return isActive;
}
